import { useMemo } from "react";
import { ButtonGroup } from "@themes/pollinator/ui/ButtonGroup";

export type TabValue = "profile" | "matching";

export type Props = {
  /** Currently active tab */
  activeTab: TabValue;
  /** Callback when tab is clicked */
  onTabChange: (tab: TabValue) => void;
  /** Whether to show the Matching Form tab */
  showMatchingTab?: boolean;
  /** Additional CSS classes */
  className?: string;
};

const ALL_TABS = [
  { label: "Public Profile", value: "profile" as const },
  { label: "Matching Survey", value: "matching" as const },
];

/**
 * Tab navigation for profile pages.
 * Uses ButtonGroup with tabs layout for consistent styling.
 * Designed to be used inside AppPageHeader's tabs prop.
 */
export function ProfileTabs({
  activeTab,
  onTabChange,
  showMatchingTab = true,
  className,
}: Props) {
  const tabs = useMemo(
    () => ALL_TABS.filter((tab) => tab.value !== "matching" || showMatchingTab),
    [showMatchingTab]
  );

  // Don't render tabs if there's only one tab
  if (tabs.length <= 1) {
    return null;
  }

  return (
    <div className={className} data-testid="profile-tabs">
      <ButtonGroup
        layout="tabs"
        buttons={tabs.map((tab) => ({
          label: tab.label,
          onClick: () => onTabChange(tab.value),
          isActive: activeTab === tab.value,
        }))}
      />
    </div>
  );
}

export default ProfileTabs;
